import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import {
  useAddContractByIDMutation,
  useGetAgentContractByIDQuery,
} from '@/apis/extended/agentApi';

type SellerAgentPropertyModalProps = {
  isOpen: boolean;
  onClose: () => void;
  agentId?: string;
};

export const SellerAgentPropertyModal = ({
  isOpen,
  onClose,
  agentId,
}: SellerAgentPropertyModalProps) => {
  const theme = useTheme();
  const [inputId, setInputId] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [propertyId, setPropertyId] = useState<string | null>(null);

  const [addContractByID] = useAddContractByIDMutation();

  const verifyPropertyId = async () => {
    if (!inputId) {
      setError('Please enter a property ID.');
      return;
    }
    if (!agentId) {
      setError('Missing agent ID.');
      return;
    }
    if (inputId.length !== 24) {
      setError('Invalid contract ID. Please enter a valid contract ID.');
      return;
    }
    setIsLoading(true);
    try {
      await addContractByID({ agentId, contractId: inputId }).unwrap();
      setInputId('');
      onClose();
    } catch (error: any) {
      setError('Property Already exist in your account');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogContent
        sx={{ py: '60px', px: '32px', width: '450px', maxWidth: '100%' }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Add New Contract
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.greyShades.grey1 }}
          >
            Enter the Contract ID to add it to your account.
          </Typography>
        </Box>
        <Box sx={{ mt: 5 }}>
          <TextField
            label="Contract ID"
            fullWidth
            size="small"
            value={inputId}
            onChange={(e) => {
              setInputId(e.target.value);
              setError('');
            }}
            helperText={error}
            error={!!error}
            disabled={isLoading}
            placeholder="Enter contract ID here..."
          />
          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
            onClick={verifyPropertyId}
            disabled={isLoading}
          >
            {isLoading ? 'Adding Contract...' : 'Add Contract'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
